import type { Insumo, Fornecedor, Movimentacao, TipoMovimentacao } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { EstoqueApi } from './estoque.api.js';

/** `<erp-movimentacao-form-modal>` — registro de entrada/saída de estoque. `fornecedor` só é exibido quando `tipo === 'entrada'` (`PRODUCT.md` — saída não tem fornecedor). */
export class ErpMovimentacaoFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _estoqueApi: EstoqueApi | null = null;
  private _aoSalvar: ((movimentacao: Movimentacao) => void) | null = null;

  private aberto = false;
  private insumos: Insumo[] = [];
  private fornecedores: Fornecedor[] = [];
  private tipo: TipoMovimentacao = 'entrada';
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set estoqueApi(valor: EstoqueApi) {
    this._estoqueApi = valor;
  }

  set aoSalvar(valor: (movimentacao: Movimentacao) => void) {
    this._aoSalvar = valor;
  }

  abrir(insumos: Insumo[], fornecedores: Fornecedor[]): void {
    this.insumos = insumos;
    this.fornecedores = fornecedores;
    this.tipo = 'entrada';
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._estoqueApi || this.enviando) return;

    const insumoId = this.valorCampo('#insumo');
    const tipo = this.valorCampo('#tipo') as TipoMovimentacao;
    const quantidade = Number(this.valorCampo('#quantidade'));
    const fornecedorId = tipo === 'entrada' ? this.valorCampo('#fornecedor') : '';
    const descricao = this.valorCampo('#descricao').trim();

    if (!insumoId) {
      this.mensagemErro = 'selecione um insumo';
      this.render();
      return;
    }
    if (!Number.isInteger(quantidade) || quantidade <= 0) {
      this.mensagemErro = 'quantidade deve ser maior que zero';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const movimentacao = await this._estoqueApi.registrarMovimentacao({
        insumoId,
        tipo,
        quantidade,
        ...(fornecedorId ? { fornecedorId } : {}),
        ...(descricao ? { descricao } : {}),
      });
      this.aberto = false;
      this._aoSalvar?.(movimentacao);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível registrar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLSelectElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Registrar movimentação</span>
        <form novalidate>
          <label>
            Insumo
            <select id="insumo">
              ${this.insumos.map((i) => `<option value="${i.id}">${escaparHtml(i.nome)} (${i.quantidadeAtual} ${escaparHtml(i.unidade)})</option>`).join('')}
            </select>
          </label>
          <label>
            Tipo
            <select id="tipo">
              <option value="entrada" ${this.tipo === 'entrada' ? 'selected' : ''}>Entrada</option>
              <option value="saida" ${this.tipo === 'saida' ? 'selected' : ''}>Saída</option>
            </select>
          </label>
          <label>Quantidade <input id="quantidade" type="number" min="1" step="1" /></label>
          ${
            this.tipo === 'entrada'
              ? `<label>
                   Fornecedor (opcional)
                   <select id="fornecedor">
                     <option value="">—</option>
                     ${this.fornecedores.map((f) => `<option value="${f.id}">${escaparHtml(f.nome)}</option>`).join('')}
                   </select>
                 </label>`
              : ''
          }
          <label>Descrição (opcional) <input id="descricao" type="text" maxlength="500" /></label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Registrar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
    this.root.querySelector('#tipo')?.addEventListener('change', (evento) => {
      this.tipo = (evento.target as HTMLSelectElement).value as TipoMovimentacao;
      this.render();
    });
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-movimentacao-form-modal', ErpMovimentacaoFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-movimentacao-form-modal': ErpMovimentacaoFormModal;
  }
}
